import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button } from 'reactstrap'
import properties from '../../../../api/properties/hooks'
import { APPLICATION_SETTINGS, MODAL_TYPES } from '../../../../common/constants'
import responseFields from '../../../../common/constants/responseFields'
import { projectResponseFields } from '../../../../common/response/project'
import { propertyResponseFields } from '../../../../common/response/property'
import { setDeleteCallback } from '../../../../Components/Modals/DeleteModal/utils'
import TableImage from '../../../../Components/TanstackTable/components/TableImage'
import { showModal } from '../../../../store/modal'
import { findEntityTitle, formatDateTime, formatNumberWithTwoDecimals } from '../../../../utils/general'
import { getOwnerFullName, renderAreaUnit, renderCurrency, renderCurrencyWithUnit } from '../../../../utils/property'
import columnWidths from '../../../Properties/hooks/useTableColumns/columnWidths'

const useTableColumns = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const currentLang = localStorage.getItem('i18nextLng')

    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    const w = useMemo(() => ({ ...columnWidths.hr, ...(columnWidths[currentLang] ?? {}) }), [currentLang])

    const { mutate: deleteProperty } = properties.DeleteProperty.useMutation()

    const handleDelete = useCallback(
        (id) => {
            const callbackId = 'delete-property'
            const payload = {
                modalType: MODAL_TYPES.DELETE_MODAL,
                modalProps: { callbackId, id },
            }

            setDeleteCallback(callbackId, () => deleteProperty({ id }))
            dispatch(showModal(payload))
        },
        [dispatch, deleteProperty]
    )

    const columns = useMemo(
        () => [
            {
                header: 'ID',
                id: propertyResponseFields.INTERNAL_APP_ID,
                accessorKey: propertyResponseFields.INTERNAL_APP_ID,
                enableSorting: true,
                size: w.internalAppId,
            },
            {
                header: t('table.header.common.mainPhoto'),
                id: 'propertyPhoto',
                enableSorting: false,
                size: w.mainPhoto,
                cell: ({ row }) => {
                    const { mainPhoto } = row.original ?? {}
                    if (!mainPhoto) return null
                    const fileDescription = mainPhoto.translations?.[currentLang]?.fileDescription ?? ''
                    return <TableImage photoId={mainPhoto.id} updated={mainPhoto.updated} alt={fileDescription} />
                },
            },
            {
                header: t('app.common.agencyId'),
                id: propertyResponseFields.INTERNAL_ID,
                accessorFn: (row) => row[propertyResponseFields.INTERNAL_ID] ?? null,
                enableSorting: true,
                size: w.internalId,
                meta: { tdClass: 'text-center' },
            },
            {
                header: t('app.common.service'),
                id: propertyResponseFields.PROPERTY_SERVICE,
                accessorFn: (row) => row[propertyResponseFields.PROPERTY_SERVICE]?.name ?? null,
                enableSorting: true,
                size: w.propertyService,
            },
            {
                header: t('app.common.status'),
                id: propertyResponseFields.PROPERTY_STATUS,
                accessorFn: (row) => row[propertyResponseFields.PROPERTY_STATUS]?.name ?? null,
                enableSorting: true,
                size: w.propertyStatus,
            },
            {
                header: t('app.common.type'),
                id: propertyResponseFields.PROPERTY_TYPE,
                accessorFn: (row) => row[propertyResponseFields.PROPERTY_TYPE]?.name ?? null,
                enableSorting: true,
                size: w.propertyType,
            },
            {
                header: t('table.header.properties.subtype'),
                id: propertyResponseFields.PROPERTY_SUB_TYPE,
                accessorFn: (row) => row[propertyResponseFields.PROPERTY_SUB_TYPE]?.name ?? null,
                enableSorting: false,
                size: w.propertySubType,
            },
            {
                header: t('app.common.country'),
                id: propertyResponseFields.COUNTRY,
                accessorFn: (row) => row[propertyResponseFields.COUNTRY]?.name ?? null,
                enableSorting: true,
                size: w.country,
            },
            {
                header: t('app.common.county'),
                id: propertyResponseFields.COUNTY,
                accessorFn: (row) => row[propertyResponseFields.COUNTY]?.name ?? null,
                enableSorting: true,
                size: w.county,
            },
            {
                header: t('app.common.city'),
                id: propertyResponseFields.CITY,
                accessorFn: (row) => row[propertyResponseFields.CITY]?.name ?? null,
                enableSorting: true,
                size: w.city,
            },
            {
                header: t('app.common.widerArea'),
                id: propertyResponseFields.WIDER_AREA,
                accessorFn: (row) => row[propertyResponseFields.WIDER_AREA]?.name ?? null,
                enableSorting: true,
                size: w.widerArea,
            },
            {
                header: t('app.common.quarter'),
                id: propertyResponseFields.QUARTER,
                accessorFn: (row) => row[propertyResponseFields.QUARTER]?.name ?? null,
                enableSorting: true,
                size: w.quarter,
            },
            {
                header: t('app.common.street'),
                id: propertyResponseFields.STREET,
                accessorKey: propertyResponseFields.STREET,
                enableSorting: true,
                size: w.street,
            },
            {
                header: t('app.common.streetNumber'),
                id: propertyResponseFields.STREET_NUMBER,
                accessorKey: propertyResponseFields.STREET_NUMBER,
                enableSorting: true,
                size: w.streetNumber,
            },
            {
                header: t('app.common.price', { currency: renderCurrency(currency) }),
                id: propertyResponseFields.PRICE,
                enableSorting: true,
                size: w.price,
                cell: ({ row }) => {
                    const val = row.original?.[propertyResponseFields.PRICE]
                    return val ? formatNumberWithTwoDecimals(val) : null
                },
            },
            {
                header: t('table.header.properties.pricePerUnit', { currencyPerUnit: renderCurrencyWithUnit(currency, areaUnit) }),
                id: propertyResponseFields.PRICE_M2,
                enableSorting: true,
                size: w.priceM2,
                cell: ({ row }) => {
                    const val = row.original?.[propertyResponseFields.PRICE_M2]
                    return val ? formatNumberWithTwoDecimals(val) : null
                },
            },
            {
                header: t('app.common.area', { areaUnit: renderAreaUnit(areaUnit) }),
                id: propertyResponseFields.AREA,
                enableSorting: true,
                size: w.area,
                cell: ({ row }) => {
                    const val = row.original?.[propertyResponseFields.AREA]
                    return val ? formatNumberWithTwoDecimals(val) : null
                },
            },
            {
                header: t('app.common.agent'),
                id: 'agent',
                enableSorting: true,
                size: w.primaryAgent,
                cell: ({ row }) => {
                    const agent = row.original?.[propertyResponseFields.PRIMARY_AGENT_ID]
                    return agent ? `${agent.firstName} ${agent.lastName}` : null
                },
            },
            {
                header: t('table.header.properties.agentMobile'),
                id: 'agentMobileNumber',
                accessorFn: (row) => row[propertyResponseFields.PRIMARY_AGENT_ID]?.mobileNumber ?? null,
                enableSorting: true,
                size: w.agentMobile,
            },
            {
                header: t('app.common.owner'),
                id: propertyResponseFields.OWNER,
                accessorFn: (row) => getOwnerFullName(row[propertyResponseFields.OWNER]?.[0]),
                enableSorting: true,
                size: w.owner,
            },
            {
                header: t('table.header.properties.ownerPhoneNumber1'),
                id: `${propertyResponseFields.OWNER}_phone1`,
                accessorFn: (row) => row?.owner?.[0]?.contactPhone?.[0]?.phoneNumber ?? null,
                enableSorting: true,
                size: w.ownerPhoneNumber1,
            },
            {
                header: t('table.header.properties.ownerPhoneNumber2'),
                id: `${propertyResponseFields.OWNER}_phone2`,
                accessorFn: (row) => row?.owner?.[0]?.contactPhone?.[1]?.phoneNumber ?? null,
                enableSorting: true,
                size: w.ownerPhoneNumber2,
            },
            {
                header: t('app.common.floorNumber'),
                id: propertyResponseFields.FLOOR_NO,
                enableSorting: false,
                size: w.floorNo,
                cell: ({ row }) => {
                    const o = row.original
                    if (!o) return null
                    const floorPos = o[propertyResponseFields.PROPERTY_FLOOR_POSITION]
                    const floor = floorPos?.length > 0 ? floorPos[0].name : (o[propertyResponseFields.FLOOR_NO] ?? '-')
                    const total = o[propertyResponseFields.FLOORS_TOTAL_NO] ? `/${o[propertyResponseFields.FLOORS_TOTAL_NO]}` : '/-'
                    return (
                        <>
                            {floor}
                            {total}
                        </>
                    )
                },
            },
            {
                header: t('app.common.rooms'),
                id: propertyResponseFields.ROOMS_NO,
                accessorKey: propertyResponseFields.ROOMS_NO,
                enableSorting: true,
                size: w.roomsNo,
            },
            {
                header: t('app.common.bedrooms'),
                id: propertyResponseFields.BEDROOMS_NO,
                accessorKey: propertyResponseFields.BEDROOMS_NO,
                enableSorting: true,
                size: w.bedroomsNo,
            },
            {
                header: t('table.header.properties.maxConstructionArea', { areaUnit: renderAreaUnit(areaUnit) }),
                id: propertyResponseFields.MAX_CONSTRUCTION_AREA,
                enableSorting: true,
                size: w.maxConstructionArea,
                cell: ({ row }) => {
                    const val = row.original?.[propertyResponseFields.MAX_CONSTRUCTION_AREA]
                    return val ? formatNumberWithTwoDecimals(val) : null
                },
            },
            {
                header: t('app.common.property.seaDistance'),
                id: propertyResponseFields.DISTANCE_SEA,
                enableSorting: true,
                size: w.distanceSea,
                cell: ({ row }) => {
                    const val = row.original?.[propertyResponseFields.DISTANCE_SEA]
                    return val ? formatNumberWithTwoDecimals(val) : null
                },
            },
            {
                header: t('app.common.property.yard', { areaUnit: renderAreaUnit(areaUnit) }),
                id: propertyResponseFields.AREA_GARDEN,
                enableSorting: true,
                size: w.areaGarden,
                cell: ({ row }) => {
                    const val = row.original?.[propertyResponseFields.AREA_GARDEN]
                    return val ? formatNumberWithTwoDecimals(val) : null
                },
            },
            {
                header: t('app.common.property.areaPlot', { areaUnit: renderAreaUnit(areaUnit) }),
                id: propertyResponseFields.PLOT_AREA,
                enableSorting: true,
                size: w.plotArea,
                cell: ({ row }) => {
                    const val = row.original?.[propertyResponseFields.PLOT_AREA]
                    return val ? formatNumberWithTwoDecimals(val) : null
                },
            },
            {
                header: t('app.common.heating'),
                id: propertyResponseFields.PROPERTY_HEATING,
                enableSorting: true,
                size: w.propertyHeating,
                cell: ({ row }) => (
                    <>
                        {row.original?.[propertyResponseFields.PROPERTY_HEATING]?.map((item) => (
                            <div key={item.id}>{item.name}</div>
                        ))}
                    </>
                ),
            },
            {
                header: t('app.common.web'),
                id: 'websiteTitle',
                enableSorting: false,
                size: w.propertyWebsite,
                cell: ({ row }) => (row.original?.propertyWebsite?.[0]?.visibility === 1 ? <i className="mdi mdi-check fs-4" /> : null),
            },
            {
                header: t('table.header.properties.elevator'),
                id: propertyResponseFields.PROPERTY_EQUIPMENT,
                enableSorting: false,
                size: w.propertyEquipment,
                cell: ({ row }) => {
                    const elevator = row.original?.[propertyResponseFields.PROPERTY_EQUIPMENT]?.find(({ name }) => name.toLowerCase() === 'lift')
                    return elevator ? <i className="mdi mdi-check fs-4" /> : null
                },
            },
            {
                header: t('app.common.property.constructionYear'),
                id: propertyResponseFields.CONSTRUCTION_YEAR,
                accessorKey: propertyResponseFields.CONSTRUCTION_YEAR,
                enableSorting: true,
                size: w.constructionYear,
            },
            {
                header: t('app.common.property.availableFrom'),
                id: propertyResponseFields.AVAILABLE_FROM_DATE,
                enableSorting: true,
                size: w.availableFromDate,
                cell: ({ row }) => formatDateTime(row.original?.[propertyResponseFields.AVAILABLE_FROM_DATE]),
            },
            {
                header: t('app.common.offerSource'),
                id: `${responseFields.PROPERTY_SUPPLY_SOURCE}_name`,
                accessorFn: (row) => row[responseFields.PROPERTY_SUPPLY_SOURCE]?.name ?? null,
                enableSorting: true,
                size: w.propertySupplySource,
            },
            {
                header: t('app.common.receiptDate'),
                id: propertyResponseFields.PROPERTY_RECEIPT_DATE,
                enableSorting: true,
                cell: ({ row }) => formatDateTime(row.original?.[propertyResponseFields.PROPERTY_RECEIPT_DATE]),
            },
            {
                header: t('app.common.updated'),
                id: propertyResponseFields.UPDATED,
                enableSorting: true,
                cell: ({ row }) => formatDateTime(row.original?.[propertyResponseFields.UPDATED]),
            },
            {
                header: t('app.common.project.label'),
                id: 'project',
                accessorFn: (row) => findEntityTitle(row.project, projectResponseFields.PROJECT_TITLE),
                enableSorting: true,
                size: w.project,
            },
            {
                header: t('app.common.property.designation'),
                id: propertyResponseFields.PROPERTY_LABEL,
                accessorKey: propertyResponseFields.PROPERTY_LABEL,
                enableSorting: false,
                size: w.propertyLabel,
            },
            {
                id: 'actions',
                enableSorting: false,
                size: w.actions,
                meta: { thDivClass: 'text-center', stopPropagation: true },
                cell: ({ row }) => (
                    <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
                        <li className="list-inline-item">
                            <Link
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t('button.view')}
                                to={`/properties/${row.original.id}`}
                                className="text-info d-inline-block"
                            >
                                <i className="mdi mdi-eye-outline fs-16"></i>
                            </Link>
                        </li>
                        <li className="list-inline-item edit">
                            <Link
                                title={t('button.edit')}
                                to={`/properties/${row.original.id}/edit`}
                                className="text-info d-inline-block edit-item-btn"
                            >
                                <i className="mdi mdi-pencil-outline fs-16"></i>
                            </Link>
                        </li>
                        <li className="list-inline-item edit">
                            <Button
                                type="button"
                                color="link"
                                title={t('button.delete')}
                                className="text-danger d-inline-block remove-item-btn p-0"
                                onClick={() => handleDelete(row.original.id)}
                            >
                                <i className="mdi mdi-trash-can-outline fs-16"></i>
                            </Button>
                        </li>
                    </ul>
                ),
            },
        ],
        [areaUnit, currency, currentLang, w, handleDelete, t]
    )

    return columns
}

export default useTableColumns
